import React from 'react';
import classnames from 'classnames';

// Components
import WpLabelled from '../labelled/WpLabelled';
import WpIcon from '../icon/WpIcon';

// Styles
import './WpSelect.scss';

interface StrictOption {
  /** Machine value of the option; this is the value passed to `onChange` */
  value: string;
  /** Human-readable text for the option */
  label: string;
  /** Option will be visible, but not selectable */
  disabled?: boolean;
}

export type SelectOption = string | StrictOption;

export interface Props {
  /** List of options or option groups to choose from */
  options?: SelectOption[];
  /** Label for the select */
  label: string;
  /** Visually hide the label */
  labelHidden?: boolean;
  /** Disable input */
  disabled?: boolean;
  /** Additional text to aide in use */
  helpText?: React.ReactNode;
  /** Example text to display as placeholder */
  placeholder?: string;
  /** ID for form input */
  id: string;
  /** Name for form input */
  name?: string;
  /** Value for form input */
  value?: string;
  /** Display an error state */
  error?: string;
  /** Callback when selection is changed */
  onChange?(selected: string): void;
  /** Callback when select is focussed */
  onFocus?(): void;
  /** Callback when focus is removed */
  onBlur?(): void;
  required?: boolean;
  showRequiredIndicator?: boolean;
}

const DEFAULT_PLACEHOLDER_VALUE = '';

const WpSelect: React.FC<Props> = ({
  options: optionsProp,
  label,
  labelHidden,
  disabled,
  helpText,
  placeholder,
  id,
  name,
  value = DEFAULT_PLACEHOLDER_VALUE,
  error,
  onChange,
  onFocus,
  onBlur,
  required,
  showRequiredIndicator = true,
}: Props) => {
  const baseClassName = 'WpSelect';

  function normalizeStringOption(option: string): StrictOption {
    return {
      label: option,
      value: option,
      disabled: false,
    };
  }

  /**
   * Converts a string option (and each string option in a Group) into
   * an Option object.
   */
  function normalizeOption(option: SelectOption): StrictOption {
    if (typeof option === 'string') {
      return normalizeStringOption(option);
    } else {
      return option;
    }
  }

  function renderSingleOption(option: StrictOption): React.ReactNode {
    const { value, label, ...rest } = option;
    return (
      <option key={value} value={value} {...rest} className={`${baseClassName}__option`}>
        {label}
      </option>
    );
  }

  const options = optionsProp || [];
  let normalizedOptions = options.map(normalizeOption);

  if (placeholder) {
    normalizedOptions = [
      {
        label: placeholder,
        value: DEFAULT_PLACEHOLDER_VALUE,
        disabled: true,
      },
      ...normalizedOptions,
    ];
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onChange) onChange(event.currentTarget.value);
  };

  const optionsMarkup = normalizedOptions.map(renderSingleOption);

  const hasError = Boolean(error);

  const selectClasses = classnames(
    `${baseClassName}__select`,
    disabled ? `${baseClassName}__select--disabled` : false,
    hasError ? `${baseClassName}__select--has-error` : false
  );

  return (
    <div className={baseClassName}>
      <WpLabelled
        label={label}
        helpText={helpText}
        error={error}
        id={id}
        labelHidden={labelHidden}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        <div className={`${baseClassName}__input`}>
          <select
            id={id}
            name={name}
            value={value}
            className={selectClasses}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={handleChange}
            aria-invalid={Boolean(error)}
          >
            {optionsMarkup}
          </select>
          <span className={`${baseClassName}__icon`}>
            <WpIcon name="chevron-down" />{' '}
          </span>
        </div>
      </WpLabelled>
    </div>
  );
};

export default WpSelect;
